import * as React from 'react';
import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import Button from 'lqa-ui/lib/Components/Button';
import Buttons from 'lqa-ui/lib/Components/Buttons';
import FormRow from 'lqa-ui/lib/Components/FormRow';
import ControlInput from 'lqa-ui/lib/Components/ControlInput';
import FrameLogin from 'lqa-ui/lib/Frames/Login';

import { getItem, removeItem, setItem } from '../../helpers/storage';
import { IUser } from '../../interfaces/user/user';
import { ResponseStatusEnum } from '../../enums/ResponseStatus';
import { useMutation } from 'redux-query-react';
import { extractErrors } from '../../helpers/request/extractErrors';
import { useHistory } from 'react-router';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../helpers/message/showMessage';
import { ErrorMessage } from '../../enums/ErrorMessage';
import { AccessType } from '../../enums/AccessType';
import { translateErrorMessage } from '../../enums/labels/errorMessage';
import { IFileUploadRestrictionDto } from '../../interfaces/fileUpload/fileUploadRestrictionDto';
import { useMutationWithAuth } from '../../hooks/useMutationWithAuth';
import { getHeaders } from '../../helpers/request/getHeaders';
import './Login.scss';
import { fetchFeatureFlagsFromAPI } from 'helpers/featureFlags/featureFlagsInitializer';
import { Analytics } from './../../common/utils/analytics';

export const ScreenLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isPasswordVerified, setPasswordVerified] = useState(false);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [confirmPasswordValue, setconfirmPasswordValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [useCurrentEmail, setCurrentEmail] = useState(false);
  const [useNewEmail, setNewEmail] = useState(false);
  const [mailVerification, setMailVerification] = useState(false);
  const [useNewPassword, setNewPassword] = useState(false);
  const [newPasswordConstraints, setNewPasswordConstraints] = useState({
    hasLength: false,
    hasNumber: false,
    hasSpecialChar: false,
    isMatch: false,
    isConstraintsPassed: false,
  });
  const { t } = useTranslation();
  const history = useHistory();

  const [{ isPending }, sendRequest] = useMutation<{
    username: string;
    password: string;
  }>((params) => ({
    url: `${Env.API_URL}/auth/login`,
    body: params,
    method: 'POST',
    options: {
      headers: getHeaders(),
    },
  }));

  const [{ isPending: isUpdateEmailPending }, updateEmail] = useMutation<{
    username: string;
    newEmail: string;
  }>((params) => ({
    url: `${Env.API_URL}/auth/updateEmail`,
    body: params,
    options: {
      method: 'PUT',
      headers: getHeaders(),
    },
  }));

  const [{ isPending: isUpdatePasswordPending }, updatePassword] = useMutation<{
    username: string;
    password: string;
  }>((params) => ({
    url: `${Env.API_URL}/auth/updatePassword`,
    body: params,
    options: {
      method: 'PUT',
      headers: getHeaders(),
    },
  }));

  const [{}, bootstrapRequest] = useMutationWithAuth(() => ({
    url: `${Env.API_URL}/bootstrap/fileUploadRestrictions`,
    options: {
      method: 'GET',
    },
  }));

  const [{ isPending: isForgotPending }, forgotRequest] = useMutation<{
    username: string;
  }>((params) => ({
    url: `${Env.API_URL}/auth/forgot`,
    body: params,
    method: 'POST',
    options: {
      headers: getHeaders(),
    },
  }));

  const handleRedirect = (result: any) => {
    if (!result.body.redirectUrl) {
      return;
    }
    const redirectPath = result.body.redirectUrl.startsWith('/')
      ? result.body.redirectUrl
      : '/' + result.body.redirectUrl;
    window.location.href = `${Env.SSO_API_URL}${redirectPath}`;
  };

  const handleEmailVerification = (user: any) => {
    setShowLogin(false);
    if (user.userEmail) {
      setUserEmail(user.userEmail);
      setCurrentEmail(true);
    } else {
      setNewEmail(true);
    }
  };

  const navigateAfterLogin = (user: any) => {
    if (history.location.pathname === '/') {
      if (
        AccessType.LQAAdmin === user.accessType ||
        AccessType.LQABasic === user.accessType
      ) {
        history.push('/admin/');
      } else if (user.isViewer === true) {
        history.push('/action-plans/action-plans/');
      } else if (
        AccessType.brandClientUser === user.accessType ||
        AccessType.masterClientUser === user.accessType ||
        AccessType.clientUser === user.accessType
      ) {
        history.push('/brand/dashboard/');
      } else {
        history.push('/hotel/dashboard/');
      }
      setItem('app-refresh', `${Date.now()}`);
    } else if (
      history.location.pathname === '/logout' ||
      history.location.pathname === '/admin/logout'
    ) {
      history.push('/');
    }
  };

  const handleSuccessfulLogin = async (result: any) => {
    rememberAuth(result.body.user, result.body.accessToken);
    rememberFileUploadRestrictions((await bootstrapRequest()).body);
    localStorage.setItem('LoginUserData', JSON.stringify(result.body));

    const brandHotelSelection = getItem('brandHotelSelection');
    const hotelId = brandHotelSelection
      ? brandHotelSelection.hotelId
      : undefined;
    try {
      await fetchFeatureFlagsFromAPI();
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error('Failed to initialize feature flags after login:', error);
    }

    Analytics.identifyUser((result.body.user || {}).id);
    Analytics.sendEventLogin();
    navigateAfterLogin(result.body.user);
    history.go(0);
  };

  const handleSubmit = useCallback(async () => {
    const result = await sendRequest({ username, password });
    const newErrors = extractErrors(result);

    if (result.status === ResponseStatusEnum.redirect) {
      handleRedirect(result);
      return;
    }

    if (newErrors) {
      setHasError(true);
      return;
    }

    if (navigator.userAgent.indexOf('MSIE') !== -1) {
      alert(
        'Microsoft has announced that they will be ending support for Internet Explorer on June 15, 2022. Our site has several features that are not currently supported by Internet Explorer. We recommend viewing our site with the latest version of a modern browser such as Google Chrome or Mozilla Firefox.'
      );
    }

    if (result.status === ResponseStatusEnum.created) {
      setPasswordVerified(!result.body.user.isPasswordUpdate);

      if (result.body.user.isEmailVerify === false) {
        handleEmailVerification(result.body.user);
        return;
      }

      await handleSuccessfulLogin(result);
    } else {
      setHasError(true);
    }
  }, [sendRequest]);

  const handleForgotSubmit = useCallback(async () => {
    const result = await forgotRequest({ username });
    if (
      result.status === ResponseStatusEnum.created ||
      result.status === ResponseStatusEnum.ok
    ) {
      showSuccessMessage(
        result.body.message ||
          t(
            'login.If an account with this email exists, you will receive a password reset link.'
          )
      );
      setUsername('');
      setHasError(false);
      setShowForgot(false);
      setShowLogin(true);
    } else {
      showErrorMessage(
        translateErrorMessage(ErrorMessage.requestErrorUnspecified)
      );
      setHasError(true);
    }
  }, [forgotRequest]);

  const handleUpdateEmail = useCallback(async () => {
    const result = await updateEmail({ username, newEmail: userEmail });
    if (result.status === ResponseStatusEnum.ok) {
      showSuccessMessage(
        t('login.Confirmation email sent for verifying ') + userEmail
      );
      setHasError(false);
      setCurrentEmail(false);
      setNewEmail(false);
      setMailVerification(true);
    } else {
      showErrorMessage(
        translateErrorMessage(ErrorMessage.requestErrorUnspecified)
      );
      setHasError(true);
    }
  }, [updateEmail]);

  const handleUpdatePassword = useCallback(async () => {
    const result = await updatePassword({
      username: userEmail,
      password: newPasswordValue,
    });
    if (result.status === ResponseStatusEnum.ok) {
      showSuccessMessage(t('login.Password has been updated'));
      setHasError(false);
      setCurrentEmail(false);
      setNewEmail(false);
      setNewPassword(false);
      setShowLogin(false);
      setMailVerification(true);
    } else {
      showErrorMessage(
        translateErrorMessage(ErrorMessage.requestErrorUnspecified)
      );
      setHasError(true);
    }
  }, [updatePassword]);

  const rememberAuth = (user: IUser, token: string) => {
    removeItem('user');
    setItem(
      'user',
      JSON.stringify({
        user,
        token,
      })
    );
  };

  const rememberFileUploadRestrictions = (
    restrictions: IFileUploadRestrictionDto
  ) => {
    removeItem('fileUploadRestrictions');
    setItem('fileUploadRestrictions', JSON.stringify(restrictions));
  };

  const handleKeypress = (e) => {
    if (e.key.toLowerCase() === 'enter' && showForgot) {
      handleForgotSubmit();
    }
    if (e.key.toLowerCase() === 'enter' && showLogin) {
      handleSubmit();
    }
  };

  const validateNewPassword = () => {
    const obj = {
      hasLength: false,
      hasNumber: false,
      hasSpecialChar: false,
      isMatch: false,
      isConstraintsPassed: false,
    };
    // Check Password Length
    if (newPasswordValue.length >= 8) {
      obj.hasLength = true;
    }
    // Check if Password has number
    if (newPasswordValue.search(/[0-9]/i) > 0) {
      obj.hasNumber = true;
    }
    // Check if Password has special character
    if (newPasswordValue.search(/[!#$%&?@^*]/i) > 0) {
      obj.hasSpecialChar = true;
    }
    // Check if Password does match with confirmPassword
    if (
      newPasswordValue.length > 0 &&
      confirmPasswordValue.length > 0 &&
      newPasswordValue === confirmPasswordValue
    ) {
      obj.isMatch = true;
    }
    if (obj.hasLength && obj.hasNumber && obj.hasSpecialChar && obj.isMatch) {
      obj.isConstraintsPassed = true;
    } else {
      obj.isConstraintsPassed = false;
    }
    setNewPasswordConstraints(obj);
  };

  React.useEffect(() => {
    validateNewPassword();
  }, [newPasswordValue, confirmPasswordValue]);

  return (
    <FrameLogin title="LQA">
      <div>
        {
          <React.Fragment>
            <div className="login-main">
              {showLogin ? (
                <div className="inner">
                  <FormRow label={t('login.Username')}>
                    <ControlInput
                      errorAnimated={true}
                      hasError={hasError}
                      value={username}
                      autoFocus={true}
                      onChange={(event) => {
                        if (hasError) {
                          setHasError(false);
                        }
                        setUsername(event.target.value);
                      }}
                      onKeyDown={handleKeypress}
                    />
                  </FormRow>
                  <FormRow label={t('login.Password')}>
                    <ControlInput
                      secureTextEntry={true}
                      hasError={hasError}
                      value={password}
                      onChange={(event) => {
                        if (hasError) {
                          setHasError(false);
                        }
                        setPassword(event.target.value);
                      }}
                      onKeyDown={handleKeypress}
                    />
                  </FormRow>
                  <Buttons layout="vertical-wide">
                    <Button
                      onClick={() => {
                        handleSubmit();
                      }}
                      title={t('login.Continue')}
                      loading={isPending}
                      intent="primary"
                    />
                    <Button
                      onClick={() => {
                        setShowLogin(false);
                        setShowForgot(true);
                      }}
                      title={t('login.Forgot password')}
                      intent="discrete"
                    />
                  </Buttons>
                </div>
              ) : (
                ''
              )}
              {showForgot ? (
                <div className="inner">
                  <FormRow label={t('login.Email address')}>
                    <ControlInput
                      errorAnimated={true}
                      hasError={hasError}
                      value={username}
                      autoFocus={true}
                      onChange={(event) => {
                        setUsername(event.target.value);
                      }}
                      onKeyDown={handleKeypress}
                    />
                  </FormRow>
                  <Buttons layout="vertical-wide">
                    <Button
                      onClick={() => {
                        handleForgotSubmit();
                      }}
                      title={t('login.Send new password')}
                      loading={isForgotPending}
                      intent="primary"
                    />
                    <Button
                      onClick={() => {
                        setShowForgot(false);
                        setShowLogin(true);
                        setHasError(false);
                      }}
                      title={t('login.Cancel')}
                      intent="discrete"
                    />
                  </Buttons>
                </div>
              ) : (
                ''
              )}
              {useCurrentEmail ? (
                <div className="inner">
                  <div className="use-current-mail">
                    <div className="content">
                      We are making our website more secure. Your username is
                      now going to be your email address. We found below email
                      address that associated with your account.
                      <br />
                      <b>{userEmail}</b>
                    </div>
                    <br />
                    <Buttons layout="vertical-wide">
                      <Button
                        onClick={() => {
                          handleUpdateEmail();
                        }}
                        title={t('login.Use This Email')}
                        loading={isUpdateEmailPending}
                        intent="primary"
                      />
                      <Button
                        onClick={() => {
                          setCurrentEmail(false);
                          setNewEmail(true);
                          setHasError(false);
                        }}
                        title={t('login.New Email')}
                        intent="discrete"
                      />
                      <Button
                        onClick={() => {
                          window.location.href = window.location.origin + '/';
                        }}
                        title={t('login.Go To Login')}
                        intent="discrete"
                      />
                    </Buttons>
                  </div>
                </div>
              ) : (
                ''
              )}
              {useNewEmail ? (
                <div className="inner">
                  <div className="use-current-mail">
                    <div className="content">
                      We are making our website more secure. Your username is
                      now going to be your email address. Please provide us with
                      a valid email address to continue logging in to the
                      account.
                    </div>
                    <br />
                    <div>
                      <FormRow label={t('login.Email:')}>
                        <ControlInput
                          errorAnimated={true}
                          hasError={hasError}
                          value={userEmail}
                          autoFocus={true}
                          onChange={(event) => {
                            if (hasError) {
                              setHasError(false);
                            }
                            setUserEmail(event.target.value);
                          }}
                          onKeyDown={handleKeypress}
                        />
                      </FormRow>
                    </div>
                    <Buttons layout="vertical-wide">
                      <Button
                        onClick={() => {
                          handleUpdateEmail();
                        }}
                        title={t('login.Continue')}
                        loading={isUpdateEmailPending}
                        intent="primary"
                      />
                      <Button
                        onClick={() => {
                          window.location.href = window.location.origin + '/';
                        }}
                        title={t('login.Go To Login')}
                        intent="discrete"
                      />
                    </Buttons>
                  </div>
                </div>
              ) : (
                ''
              )}
              {useNewPassword ? (
                <div className="inner">
                  <div className="use-current-mail">
                    <div className="password-screen-content">
                      We're also increasing our requirements for Password
                      security. Password must now have:
                    </div>
                    <div className="password-screen-content">
                      <div
                        className={
                          newPasswordConstraints.hasLength
                            ? 'label-success'
                            : 'label-error'
                        }
                      >
                        - 8 characters
                      </div>
                      <div
                        className={
                          newPasswordConstraints.hasNumber
                            ? 'label-success'
                            : 'label-error'
                        }
                      >
                        - At least one number
                      </div>
                      <div
                        className={
                          newPasswordConstraints.hasSpecialChar
                            ? 'label-success'
                            : 'label-error'
                        }
                      >
                        - At least one special character
                      </div>
                      <div
                        className={
                          newPasswordConstraints.isMatch
                            ? 'label-success'
                            : 'label-error'
                        }
                      >
                        - Passwords Must Match
                      </div>
                    </div>
                    <div>
                      <FormRow label={t('login.New Password:')}>
                        <ControlInput
                          errorAnimated={true}
                          secureTextEntry={true}
                          hasError={hasError}
                          value={newPasswordValue}
                          autoFocus={true}
                          onChange={(event) => {
                            if (hasError) {
                              setHasError(false);
                            }
                            setNewPasswordValue(event.target.value);
                          }}
                        />
                      </FormRow>
                    </div>
                    <div>
                      <FormRow label={t('login.Confirm Password:')}>
                        <ControlInput
                          errorAnimated={true}
                          secureTextEntry={true}
                          hasError={hasError}
                          value={confirmPasswordValue}
                          onChange={(event) => {
                            if (hasError) {
                              setHasError(false);
                            }
                            setconfirmPasswordValue(event.target.value);
                          }}
                        />
                      </FormRow>
                    </div>
                    <Buttons layout="vertical-wide">
                      <Button
                        onClick={() => {
                          handleUpdatePassword();
                        }}
                        title={t('login.Continue')}
                        disabled={
                          newPasswordConstraints.isConstraintsPassed === false
                        }
                        loading={isPending}
                        intent="primary"
                      />
                    </Buttons>
                  </div>
                </div>
              ) : (
                ''
              )}
              {mailVerification ? (
                <div className="inner">
                  <div className="use-current-mail">
                    <div className="password-screen-content">
                      Great! We've send an email to {userEmail} to confirm.
                      Click on the link in the email to complete the process.
                    </div>
                    <Buttons layout="vertical-wide">
                      <Button
                        onClick={() => {
                          setMailVerification(false);
                          setNewEmail(true);
                        }}
                        title={t('login.Change Email')}
                        intent="discrete"
                      />
                      <Button
                        onClick={() => {
                          handleUpdateEmail();
                        }}
                        title={t('login.Resend Email')}
                        loading={isUpdateEmailPending}
                        intent="discrete"
                      />
                      <Button
                        onClick={() => {
                          window.location.href = window.location.origin + '/';
                        }}
                        title={t('login.Go To Login')}
                        intent="discrete"
                      />
                    </Buttons>
                  </div>
                </div>
              ) : (
                ''
              )}
            </div>
          </React.Fragment>
        }
      </div>
    </FrameLogin>
  );
};

export default ScreenLogin;
